import type { ComponentType } from 'react';
import {
  CalendarBlankIcon,
  CursorClickIcon,
  EnvelopeSimpleIcon,
  IdentificationBadgeIcon,
  ImageSquareIcon,
  ListBulletsIcon,
  RocketLaunchIcon,
  TextAaIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react';

import type {
  BlockType,
  ButtonBlockContent,
  CalendarBlockContent,
  ContactBlockContent,
  HeroBlockContent,
  ImageBlockContent,
  LeadershipBlockContent,
  ListBlockContent,
  TextBlockContent,
  UnitIdentityBlockContent,
} from '@/types/database';

import { HeroBlockView } from './hero/HeroBlockView';
import { HeroBlockEditPanel } from './hero/HeroBlockEditPanel';
import { TextBlockView } from './text/TextBlockView';
import { TextBlockEditPanel } from './text/TextBlockEditPanel';
import { ImageBlockView } from './image/ImageBlockView';
import { ImageBlockEditPanel } from './image/ImageBlockEditPanel';
import { CalendarBlockView } from './calendar/CalendarBlockView';
import { CalendarBlockEditPanel } from './calendar/CalendarBlockEditPanel';
import { ContactBlockView } from './contact/ContactBlockView';
import { ContactBlockEditPanel } from './contact/ContactBlockEditPanel';
import { ButtonBlockView } from './button/ButtonBlockView';
import { ButtonBlockEditPanel } from './button/ButtonBlockEditPanel';
import { ListBlockView } from './list/ListBlockView';
import { ListBlockEditPanel } from './list/ListBlockEditPanel';
import { LeadershipBlockView } from './leadership/LeadershipBlockView';
import { LeadershipBlockEditPanel } from './leadership/LeadershipBlockEditPanel';
import { UnitIdentityBlockView } from './unit-identity/UnitIdentityBlockView';
import { UnitIdentityBlockEditPanel } from './unit-identity/UnitIdentityBlockEditPanel';

export interface BlockRegistryEntry<T> {
  label: string;
  description: string;
  icon: ComponentType<{ size?: number }>;
  // 'component': a thin, directly-configurable MUI primitive (adding it
  // means configuring a real MUI component's own props). 'widget': pulls in
  // other platform data or composes several primitives into a fixed section
  // — that's what earns it a bespoke content shape instead.
  category: 'component' | 'widget';
  // Set only for blocks genuinely bound to live Scouting Ops data (a
  // narrower set than category: 'widget' — hero/contact are widgets
  // because they compose primitives, not because they're bound). Consumed
  // by the Data screen (src/components/studio/DataDrawer.tsx) to
  // distinguish "pulled from Scouting Ops" content from manually-authored
  // content, per docs/site-builder-overhaul.md's Phase B/D.
  boundTo?: string;
  defaultContent: T;
  View: ComponentType<{ content: T; basePath: string }>;
  EditPanel: ComponentType<{ content: T; onUpdate: (content: T) => void }>;
}

interface BlockContentMap {
  hero: HeroBlockContent;
  text: TextBlockContent;
  image: ImageBlockContent;
  calendar: CalendarBlockContent;
  contact: ContactBlockContent;
  button: ButtonBlockContent;
  list: ListBlockContent;
  leadership: LeadershipBlockContent;
  'unit-identity': UnitIdentityBlockContent;
}

// A single BlockType -> presentation/editing mapping consumed by both the
// live editor canvas and the public site, so what an admin edits is exactly
// what visitors see — no separate rendering path to keep in sync.
export const blockRegistry: { [K in BlockType]: BlockRegistryEntry<BlockContentMap[K]> } = {
  hero: {
    label: 'Hero',
    description: 'Welcome banner with headline',
    icon: RocketLaunchIcon,
    category: 'widget',
    defaultContent: {} as HeroBlockContent,
    View: HeroBlockView,
    EditPanel: HeroBlockEditPanel,
  },
  text: {
    label: 'Text',
    description: 'Rich text content',
    icon: TextAaIcon,
    category: 'component',
    defaultContent: { content: '' } as TextBlockContent,
    View: TextBlockView,
    EditPanel: TextBlockEditPanel,
  },
  image: {
    label: 'Image',
    description: 'Single image with caption',
    icon: ImageSquareIcon,
    category: 'component',
    defaultContent: { imageUrl: '' } as ImageBlockContent,
    View: ImageBlockView,
    EditPanel: ImageBlockEditPanel,
  },
  calendar: {
    label: 'Calendar',
    description: 'Upcoming events list',
    icon: CalendarBlankIcon,
    category: 'widget',
    boundTo: 'Scouting Ops Calendar',
    defaultContent: { heading: 'Upcoming Events' } as CalendarBlockContent,
    View: CalendarBlockView,
    EditPanel: CalendarBlockEditPanel,
  },
  contact: {
    label: 'Contact Form',
    description: 'Safe contact form',
    icon: EnvelopeSimpleIcon,
    category: 'widget',
    defaultContent: {} as ContactBlockContent,
    View: ContactBlockView,
    EditPanel: ContactBlockEditPanel,
  },
  button: {
    label: 'Button',
    description: 'Call-to-action link',
    icon: CursorClickIcon,
    category: 'component',
    defaultContent: { text: '', link: '' } as ButtonBlockContent,
    View: ButtonBlockView,
    EditPanel: ButtonBlockEditPanel,
  },
  list: {
    label: 'List',
    description: 'Bulleted information',
    icon: ListBulletsIcon,
    category: 'component',
    defaultContent: { items: [] } as ListBlockContent,
    View: ListBlockView,
    EditPanel: ListBlockEditPanel,
  },
  leadership: {
    label: 'Leadership Directory',
    description: 'Adult leaders, bound to your roster',
    icon: UsersThreeIcon,
    category: 'widget',
    boundTo: 'Scouting Ops Roster',
    defaultContent: { heading: 'Our Leadership' } as LeadershipBlockContent,
    View: LeadershipBlockView,
    EditPanel: LeadershipBlockEditPanel,
  },
  'unit-identity': {
    label: 'Unit Identity Card',
    description: 'Unit facts and meeting schedule, bound to your unit record',
    icon: IdentificationBadgeIcon,
    category: 'widget',
    boundTo: 'Unit Record',
    defaultContent: { showMeetingSchedule: true } as UnitIdentityBlockContent,
    View: UnitIdentityBlockView,
    EditPanel: UnitIdentityBlockEditPanel,
  },
};

export const blockTypes = Object.keys(blockRegistry) as BlockType[];
